import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { showAppAlert } from '../utils/alertService';
import LoaderOverlay from '../components/LoaderOverlay';
import '../styles/formStyles.css';

const API_URL = process.env.REACT_APP_API_BASE_URL;

function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState(null);
  const [email, setEmail] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);

  useEffect(() => {
    const resetToken = searchParams.get('token');
    const resetEmail = searchParams.get('email');

    if (!resetToken || !resetEmail) {
      showAppAlert('error', 'Link de redefinição inválido ou incompleto.');
      navigate('/login');
      return;
    }
    setToken(resetToken);
    setEmail(resetEmail);
  }, [searchParams, navigate]); // Adicionado email como dependência implícita via searchParams

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      showAppAlert('warning', 'A senha deve ter no mínimo 6 caracteres.');
      return;
    }
    if (password !== confirmPassword) {
      showAppAlert('warning', 'As senhas não coincidem.');
      return;
    }
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/password/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, token, password, password_confirmation: password }),
      });

      const data = await response.json();

      if (response.ok) {
        showAppAlert('success', 'Sua senha foi redefinida com sucesso! Faça o login com sua nova senha.');
        navigate('/login');
      } else {
        showAppAlert('error', data.message || 'Não foi possível redefinir a senha. O link pode ter expirado.');
      }
    } catch (error) {
      showAppAlert('error', 'Ocorreu um erro. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <LoaderOverlay isLoading={loading} message="Redefinindo..." />
      <div className="form-container">
        <img src="/logo-fidelity.png" alt="Logotipo" />
        <h2>Redefinir Senha</h2>
        <form onSubmit={handleSubmit}>
          <div className="label-style">
            <label>Nova Senha:</label>
            <span style={{ position: 'relative', display: 'inline-block', width: '100%', maxWidth: '400px' }}>
              <input
                type={isPasswordVisible ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-style input-medium"
                required
              />
              <span
                onMouseDown={() => setIsPasswordVisible(true)}
                onMouseUp={() => setIsPasswordVisible(false)}
                onMouseLeave={() => setIsPasswordVisible(false)}
                onTouchStart={() => setIsPasswordVisible(true)}
                onTouchEnd={() => setIsPasswordVisible(false)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  userSelect: 'none',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {isPasswordVisible ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8A2BE2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8A2BE2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                )}
              </span>
            </span>
          </div>
          <div className="label-style">
            <label>Confirmar Nova Senha:</label>
            <span style={{ position: 'relative', display: 'inline-block', width: '100%', maxWidth: '400px' }}>
              <input
                type={isConfirmPasswordVisible ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-style input-medium"
                required
              />
              <span
                onMouseDown={() => setIsConfirmPasswordVisible(true)}
                onMouseUp={() => setIsConfirmPasswordVisible(false)}
                onMouseLeave={() => setIsConfirmPasswordVisible(false)}
                onTouchStart={() => setIsConfirmPasswordVisible(true)}
                onTouchEnd={() => setIsConfirmPasswordVisible(false)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  userSelect: 'none',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {isConfirmPasswordVisible ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8A2BE2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8A2BE2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                )}
              </span>
            </span>
          </div>
          <button type="submit" className="button-style" disabled={loading}>
            Salvar
          </button>
        </form>
      </div>
    </div>
  );
}

export default ResetPassword;